import { useState, useMemo } from 'react';
import { LtButton, LtModal, DynamicMultiselector, ExpandableTable, LtIcon, IndicatorModal } from 'limetray-ui-kit';
import { userManagerConstants } from '../../../../constants/userManagerConstants';

const { createRoleModal } = userManagerConstants;

/**
 * RolePermissionsSection
 *
 * Renders the "Permissions" section for role create/edit.
 * Accepts mapped permissions data from the parent component to prevent redundant API calls.
 */
function RolePermissionsSection({
  selectedPermissionRoleIds = [], // format: Array of string/number roleIds e.g. ['142', '205']
  onChange,
  mode,
  roleGroupId,
  initialPermissionRoleIds = [],
  permissionsHierarchy = [],
  flatPermissions = [],
  permissionsLoading = false,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  // Convert incoming selectedPermissionRoleIds to string array for DynamicMultiselector
  const selectedRoleIdsStrings = useMemo(() => {
    return (selectedPermissionRoleIds || []).map(String);
  }, [selectedPermissionRoleIds]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleSaveSelection = (selectedIds) => {
    const normalizedIds = selectedIds.map(String);
    onChange(normalizedIds);
    setIsModalOpen(false);
  };

  const handleConfirmDelete = () => {
    if (!pendingDeleteId) return;

    const updatedIds = selectedRoleIdsStrings.filter(id => id !== pendingDeleteId);
    onChange(updatedIds);
    setPendingDeleteId(null);
  };

  const hasSelected = selectedRoleIdsStrings.length > 0;
  const buttonLabel = (mode === 'edit' && hasSelected)
    ? createRoleModal.sectionTitles.permissions.editButton
    : createRoleModal.sectionTitles.permissions.button;

  const tableData = useMemo(() => {
    return selectedRoleIdsStrings
      .map((roleId) => {
        return flatPermissions.find((p) => String(p.roleId) === String(roleId));
      })
      .filter(Boolean);
  }, [selectedRoleIdsStrings, flatPermissions]);

  const columns = useMemo(() => [
    {
      header: createRoleModal.sectionTitles.permissions.tableHeaders.productName,
      accessor: (row) => row.productName || '',
      width: '25%',
    },
    {
      header: createRoleModal.sectionTitles.permissions.tableHeaders.featureName,
      accessor: (row) => row.featureName || '',
      width: '30%',
    },
    {
      header: createRoleModal.sectionTitles.permissions.tableHeaders.permission,
      accessor: (row) => row.roleName || '',
      width: '30%',
    },
    {
      header: createRoleModal.sectionTitles.permissions.tableHeaders.actions,
      width: '15%',
      accessor: (row) => (
        <div className="action-buttons-wrapper">
          <LtButton
            transparent
            customStyles={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '6px'
            }}
            leftElement={
              <LtIcon
                useReactIcon
                reactIcon={userManagerConstants.icons.trashIcon}
                size="tiny"
              />
            }
            onClick={() => setPendingDeleteId(row.roleId)}
          />
        </div>
      ),
    },
  ], []);

  return (
    <div className="inputGrid">
      <div className="row">
        <div className="modal-form-field">
          <div className="modal-section-header">
            <span className="modal-section-row-title">{createRoleModal.sectionTitles.permissions.inner}</span>
            <LtButton
              label={buttonLabel}
              onClick={handleOpenModal}
              size="small"
              leftElement={
                (mode === 'edit' && hasSelected) ?
                  <LtIcon
                    useReactIcon
                    reactIcon={userManagerConstants.icons.editIcon}
                    color='#00cd7c'
                    size='mini'
                  />
                  : <span className="plusIcon">+</span>
              }
              customStyles={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                backgroundColor: '#ffffff',
                color: '#00cd7c',
                padding: 0,
                gap: '6px',
              }}
            />
          </div>

          <div className="modal-section-body-margin">
            {selectedRoleIdsStrings.length === 0 ? (
              <p className="modal-section-placeholder-text">{createRoleModal.sectionTitles.permissions.emptySelection}</p>
            ) : (
              <div className="table-wrapper">
                <ExpandableTable
                  tableData={tableData}
                  columns={columns}
                  expandable={false}
                  maxHeight="400px"
                  paginationEnabled={false}
                  tableHeaderStyles={{
                    background: 'white',
                  }}
                  tableHeaderCellStyles={{
                    color: '#6b7280',
                    fontSize: '12px',
                    fontWeight: 500,
                  }}
                  tableBodyCellStyles={{
                    padding: '12px 16px',
                  }}
                  emptyMessage={createRoleModal.sectionTitles.permissions.emptyTableMessage}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <LtModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        className="permissions-modal"
        header={createRoleModal.sectionTitles.permissions.button}
        width="800px"
      >
        <div className="multiselector-modal-wrapper">
          <DynamicMultiselector
            isMultiSelector={true}
            showCloseButton={false}
            showSaveButton={true}
            loading={permissionsLoading}
            data={{
              dataSchema: {
                parentId: 'id',
                parentName: 'name',
                subParentElement: 'features',
                childrenElement: 'accesses',
                childId: 'roleId',
                childName: 'roleName',
                allowEmptyParents: true,
              },
              filterDataList: permissionsHierarchy,
              searchChildPlaceholder: createRoleModal.sectionTitles.permissions.searchChildPlaceholder,
              searchPlaceholder: createRoleModal.sectionTitles.permissions.searchPlaceholder,
              childNoDataMessage: createRoleModal.sectionTitles.permissions.childNoDataMessage,
              selectedIds: selectedRoleIdsStrings,
              showChildSearchInput: true,
              title: createRoleModal.sectionTitles.permissions.button,
              viewOnly: false
            }}
            onSelectionChange={() => { }}
            filteredDataList={permissionsHierarchy}
            handleSearch={() => { }}
            onClose={() => setIsModalOpen(false)}
            onGroupChange={() => { }}
            onSave={handleSaveSelection}
            updateMultiSelectorDataInStore={() => { }}
          />
        </div>
      </LtModal>

      <IndicatorModal
        open={pendingDeleteId !== null}
        title={createRoleModal.deleteConfirm.permission.header}
        message={createRoleModal.deleteConfirm.permission.content}
        variant="warning"
        onClose={() => setPendingDeleteId(null)}
        actions={[
          {
            label: createRoleModal.deleteConfirm.permission.cancelButton,
            onClick: () => setPendingDeleteId(null),
            backgroundColor: '#ffffff',
						color: '#374151',
						borderColor: '#efefef',
          },
          {
            label: createRoleModal.deleteConfirm.permission.confirmButton,
            onClick: handleConfirmDelete,
            backgroundColor: '#ef4444',
            color: '#ffffff',
          },
        ]}
        actionsAlignment="center"
        maxWidth="420px"
      />
    </div>
  );
}

export default RolePermissionsSection;
